import { ClockIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import type { Color } from 'stories/types'

function OptionHoverTip({ text }: { text: string }) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null)

  return (
    <span
      className="relative ml-1.5 inline-flex items-center shrink-0"
      onMouseEnter={(e) => setAnchor(e.currentTarget.getBoundingClientRect())}
      onMouseLeave={() => setAnchor(null)}
    >
      <QuestionMarkCircleIcon className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
      {anchor &&
        createPortal(
          <span
            role="tooltip"
            className="fixed z-[9999] w-72 max-w-[min(18rem,calc(100vw-1rem))] rounded-md bg-gray-900 px-3 py-2 text-left text-xs leading-relaxed text-white shadow-lg whitespace-normal pointer-events-none"
            style={{
              top: anchor.top + anchor.height / 2,
              left: Math.min(anchor.right + 8, window.innerWidth - 296),
              transform: 'translateY(-50%)',
            }}
          >
            {text}
          </span>,
          document.body,
        )}
    </span>
  )
}

interface RadioGroupProps {
  /**
   * What background color to use
   */
  color?: Color
  /**
   * Is Full
   */
  full?: boolean
  /**
   * Is disabled
   */
  disabled?: boolean
  /**
   * Id of Input
   */
  id: string
  /**
   * Title of Input
   */
  title?: string
  /**
   * Name of Input
   */
  name?: string
  /**
   * Value of Input
   */
  value?: string
  /**
   * Error of Input
   */
  error?: string
  /**
   * Size of Input
   */
  size?: number
  /**
   * Options of select
   */
  options?: Array<string> | Record<string, string> // (Title => key)
  /**
   * Per-option hover tooltips (key => message). Shown via info icon so radios stay clickable.
   */
  optionTooltips?: Record<string, string>
  /**
   * Custom class name
   */
  className?: string
  /**
   * Required
   */
  required?: boolean
  /**
   * Optional history handler
   */
  history?: boolean

  titleType?: string
  isPlain?: boolean
  noBorder?: boolean
  noPadding?: boolean
  additionalElements?: JSX.Element | null
  /**
   * Optional click handler
   */
  onChange?: (value: any) => void
  onClick?: (value: any) => void
  showHistory?: () => void
}

/**
 * Primary UI component for user interaction
 */
export const RadioGroup = ({
  color = 'shade-blue',
  disabled = false,
  id = '',
  title = '',
  name = '',
  value = '',
  error = '',
  options = [],
  optionTooltips = {},
  className = '',
  size = 4,
  required = false,
  history = false,
  titleType = 'absolute',
  isPlain = false,
  noBorder = false,
  noPadding = false,
  additionalElements = null,
  onChange = () => {},
  onClick = () => {},
  showHistory = () => {},
  ...props
}: RadioGroupProps) => {
  let classNames = `block rounded py-1.5 px-2 w-full text-sm text-gray-900 bg-transparent border border-gray-200 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer`
  if (size === 3) {
    classNames = `block focus:ring-0 text-[13px] text-black bg-white py-0 pl-1 pr-4 bg-[center_right_0rem] w-full border-gray-300`
  }
  if (isPlain) classNames += ' !flex gap-4'
  if (noBorder) classNames += ' !border-0'
  if (noPadding) classNames += ' !p-0'

  const renderOptionTip = (key: string) => {
    const tip = optionTooltips?.[key]
    if (!tip) return null
    return <OptionHoverTip text={tip} />
  }

  return (
    <div className="input-container -mt-2 mb-2">
      <div className={`relative z-0 pt-4 w-full group ${className} ${disabled ? 'bg-gray-100' : ''}`}>
        {title.length > 0 &&
          (titleType == 'absolute' ? (
            <label
              htmlFor={id}
              className={`absolute text-xs text-gray-700 dark:text-gray-400 duration-300 transform -translate-y-2 top-3 origin-[0] peer-focus:left-0 peer-focus:text-${color} peer-focus:dark:text-${color} peer-placeholder-shown:text-sm peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:text-xs peer-focus:-translate-y-6 peer-placeholder-shown:leading-3 ml-2 px-1 rounded bg-white z-10 peer-placeholder-shown:-z-10 peer-focus:z-10 left-0`}
            >
              {title}
              {required && '*'}
            </label>
          ) : (
            <label className="text-sm font-medium text-gray-900 dark:text-gray-300 flex-auto">
              {title}
              {required && '*'}
            </label>
          ))}
        <div className={classNames} {...props}>
          {Array.isArray(options) &&
            options.map((option) => (
              <div className="flex items-center my-2" key={`option-${name}-${option}`}>
                <input
                  type="radio"
                  id={`${name}-${option}`}
                  name={name}
                  value={option}
                  key={`${name}-${option}`}
                  disabled={disabled}
                  onChange={(e) => onChange(e.currentTarget.value)}
                  onClick={(e) => onClick(e.currentTarget.value)}
                  required={required}
                  checked={value === option}
                  className={`checked:bg-${color} text-${color} focus:ring-${color}`}
                />
                <label htmlFor={`${name}-${option}`} className="ml-1">
                  {option}
                </label>
                {renderOptionTip(option)}
              </div>
            ))}

          {!Array.isArray(options) &&
            Object.keys(options).map((key) => (
              <div className="flex items-center my-2" key={`option-${key}`}>
                <input
                  type="radio"
                  id={`${name}-${key}`}
                  name={name}
                  value={key}
                  key={`${name}-${key}`}
                  disabled={disabled}
                  onChange={(e) => onChange(e.currentTarget.value)}
                  onClick={(e) => onClick(e.currentTarget.value)}
                  required={required}
                  checked={value === key}
                  className={`checked:bg-${color} text-${color} focus:ring-${color}`}
                />
                <label htmlFor={`${name}-${key}`} className="ml-1 cursor-pointer">
                  <span dangerouslySetInnerHTML={{ __html: options[key] }} />
                </label>
                {renderOptionTip(key)}
              </div>
            ))}
        </div>
        {history && (
          <span className="ml-1 hidden group-hover:inline absolute right-2 top-0" onClick={() => showHistory()}>
            <ClockIcon className="h-[14px] w-[14px] text-gray-500 cursor-pointer" aria-hidden="true" />
          </span>
        )}
        {additionalElements && (
          <span className="absolute right-2 top-0 inline-flex items-center">{additionalElements}</span>
        )}
      </div>
      {error && <p className="peer-invalid:visible text-rose-700 text-xs pl-1">{error}</p>}
    </div>
  )
}
